import React, { useState } from 'react'

import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from '@material-ui/core'
import { ChevronLeft, ChevronRight } from '@material-ui/icons'
import PropTypes from 'prop-types'

import { Card } from 'components'
import { AttemptDetails } from './components'
import useStyles from './styles'
import helpers from 'helpers'

const findBestAttempt = (attempts) =>
  attempts.reduce((best, current) => {
    if (!best) return current
    return Number(current?.score) >= Number(best?.score) ? current : best
  }, null)

const ExerciseReview = ({
  attempts,
  focusAttemptId,
  canAttempt,
  remainingAttempts,
  onRetake,
  prevLessonId,
  onPrevLesson,
  onNextLesson,
  isLastLesson,
}) => {
  const classes = useStyles()

  const bestAttempt = findBestAttempt(attempts)
  const [expandedAttemptId] = useState(
    () => focusAttemptId ?? bestAttempt?.id ?? null,
  )
  const [isRetakeDialogOpen, setIsRetakeDialogOpen] = useState(false)

  const roundedBestScore = Math.round(bestAttempt?.score ?? 0)
  const summaryTitle = canAttempt ? 'Continue estudando' : 'Resultado final'

  const handleRetakeClick = () => setIsRetakeDialogOpen(true)

  const handleRetakeCancel = () => setIsRetakeDialogOpen(false)

  const handleRetakeConfirm = () => {
    setIsRetakeDialogOpen(false)
    onRetake()
  }

  const remainingAttemptsText = `Resta ${remainingAttempts} ${
    remainingAttempts < 2 ? 'tentativa' : 'tentativas'
  }`

  const retakeDialogText = `A nota que vale é sempre a sua maior (${roundedBestScore}%). Se tirar uma nota menor agora, não muda nada no seu certificado só atualiza se você tirar uma nota maior. Tem certeza que quer refazer a avaliação?`

  return (
    <Box>
      <Typography className={classes.pageTitle}>
        Revisão das perguntas
      </Typography>

      {attempts.map((attemptItem, index) => {
        const attemptNumber = index + 1
        const isBest = attemptItem?.id === bestAttempt?.id
        const isExpanded = expandedAttemptId === attemptItem?.id
        const attemptScore = Math.round(attemptItem?.score ?? 0)

        return (
          <Card
            key={attemptItem?.id}
            title={`Tentativa ${attemptNumber} -  ${helpers.formatters.ptBrFormatDateTime(
              attemptItem?.createdAt,
            )}`}
            dropdown
            expandedOption={isExpanded}
            actionButton={
              isBest ? (
                <Chip
                  label="Melhor nota"
                  size="small"
                  className={classes.bestChip}
                />
              ) : null
            }
          >
            <Box className={classes.attemptCardContent}>
              <AttemptDetails
                attempt={attemptItem}
                summaryTitle={summaryTitle}
                attemptScore={attemptScore}
              />
            </Box>
          </Card>
        )
      })}
      {remainingAttempts !== null && (
        <Typography variant="body1" className={classes.remainingAttempts}>
          {remainingAttemptsText}
        </Typography>
      )}
      <Box className={classes.navigation}>
        <Button
          variant="outlined"
          size="small"
          startIcon={<ChevronLeft />}
          onClick={onPrevLesson}
          disabled={!prevLessonId}
          className={classes.prevButton}
        >
          ANTERIOR
        </Button>

        <Box className={classes.actionsRight}>
          {canAttempt && (
            <Button
              variant="outlined"
              size="small"
              onClick={handleRetakeClick}
              className={classes.retakeButton}
            >
              REFAZER AVALIAÇÃO
            </Button>
          )}
          <Button
            variant="contained"
            size="small"
            endIcon={<ChevronRight />}
            onClick={onNextLesson}
            className={classes.nextButton}
          >
            {isLastLesson ? 'FINALIZAR CURSO' : 'PRÓXIMO'}
          </Button>
        </Box>
      </Box>

      <Dialog open={isRetakeDialogOpen} onClose={handleRetakeCancel}>
        <DialogTitle>Refazer avaliação?</DialogTitle>
        <DialogContent>
          <DialogContentText>{retakeDialogText}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleRetakeCancel} className={classes.prevButton}>
            Cancelar
          </Button>
          <Button
            onClick={handleRetakeConfirm}
            variant="contained"
            className={classes.nextButton}
          >
            Refazer mesmo assim
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

ExerciseReview.propTypes = {
  attempts: PropTypes.array,
  focusAttemptId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  canAttempt: PropTypes.bool,
  remainingAttempts: PropTypes.number,
  onRetake: PropTypes.func.isRequired,
  prevLessonId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onPrevLesson: PropTypes.func.isRequired,
  onNextLesson: PropTypes.func.isRequired,
  isLastLesson: PropTypes.bool,
}

ExerciseReview.defaultProps = {
  attempts: [],
  remainingAttempts: null,
}

export default ExerciseReview
